'use client';

import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/popover/popover';
import { cn } from '@/libs/shadcn';

export type SelectOption<T = string> = {
  value: T;
  label: string;
};

export interface SelectProps<T = string> {
  /** Options to display */
  options: SelectOption<T>[];
  /** Current value (controlled) */
  value?: T;
  /** Default value (uncontrolled) */
  defaultValue?: T;
  /** Callback when selection changes */
  onValueChange?: (value: T) => void;
  /** Label prefix shown in trigger (e.g. "Status" → "Status : ") */
  label?: string;
  /** Placeholder when no value selected */
  placeholder?: string;
  /** Custom render for the trigger (selected display) */
  renderSelected?: (option: SelectOption<T> | null) => React.ReactNode;
  /** Custom render for each option in the list */
  renderOption?: (option: SelectOption<T>, index: number) => React.ReactNode;
  /** Optional class for trigger */
  className?: string;
  /** Optional class for content */
  contentClassName?: string;
  /** Optional class for item */
  itemClassName?: string;
  /** Disabled state */
  disabled?: boolean;
}

function DefaultRenderSelected<T>({
  option,
  label,
  placeholder,
  open,
}: {
  option: SelectOption<T> | null;
  label?: string;
  placeholder?: string;
  open?: boolean;
}) {
  const displayText = option ? option.label : (placeholder ?? 'Pilih');
  return (
    <span className="flex w-full items-center justify-between gap-2 text-sm">
      {label != null && label !== '' ? (
        <>
          <span className="text-muted-foreground">{label} : </span>
          <span className="text-foreground font-medium">{displayText}</span>
        </>
      ) : (
        <span className="text-foreground">{displayText}</span>
      )}
      <ChevronDown
        className={cn(
          'size-4 shrink-0 text-muted-foreground transition-transform duration-200',
          open && 'rotate-180'
        )}
      />
    </span>
  );
}

function DefaultRenderOption<T>({ option }: { option: SelectOption<T> }) {
  return <span className="text-sm text-inherit">{option.label}</span>;
}

function Select<T = string>({
  options,
  value,
  defaultValue,
  onValueChange,
  label,
  placeholder = 'Semua',
  renderSelected,
  renderOption,
  className,
  contentClassName,
  itemClassName,
  disabled,
}: SelectProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<T | undefined>(
    defaultValue
  );
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const selectedOption = React.useMemo(
    () => options.find(o => o.value === currentValue) ?? null,
    [options, currentValue]
  );

  const handleSelect = React.useCallback(
    (option: SelectOption<T>) => {
      if (!isControlled) setInternalValue(option.value);
      onValueChange?.(option.value);
      setOpen(false);
    },
    [isControlled, onValueChange]
  );

  const triggerContent =
    renderSelected != null ? (
      renderSelected(selectedOption)
    ) : (
      <DefaultRenderSelected
        option={selectedOption}
        label={label}
        placeholder={placeholder}
        open={open}
      />
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        disabled={disabled}
        className={cn(
          'flex min-w-[120px] min-h-9 items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2 text-left text-sm outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
          open && 'border-primary ring-2 ring-primary/20',
          className
        )}
      >
        {triggerContent}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn('min-w-[120px] w-(--anchor-width) p-1', contentClassName)}
      >
        <div className="flex flex-col gap-0.5">
          {options.map((option, index) =>
            renderOption != null ? (
              <div key={index + 1} onClick={() => handleSelect(option)}>
                {renderOption(option, index)}
              </div>
            ) : (
              <button
                type="button"
                key={index + 1}
                onClick={() => handleSelect(option)}
                className={cn(
                  'flex w-full items-center rounded-md px-3 py-1.5 text-left outline-none transition-colors cursor-pointer text-foreground hover:bg-primary/20 focus-visible:bg-primary/20 focus-visible:text-foreground hover:text-foreground',
                  currentValue === option.value &&
                    'bg-primary text-primary-foreground font-medium',
                  itemClassName
                )}
              >
                <DefaultRenderOption option={option} />
              </button>
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default Select;
